import React from 'react'
import PropTypes from 'prop-types'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Tooltip,
} from 'chart.js'
import { useThemeContext } from '../../theme/ThemeContext'
import { getDesignTokens } from '../../theme/linearTheme'
import formatDay, { shortDay } from './formatDay'
import { isScored } from './cardModel'
import { chartLabel, NO_SCORE } from './copy'
import { axisPositions, labelRotation } from './timeAxis'

ChartJS.register(LineElement, PointElement, LinearScale, Tooltip)

const TICK_FONT_SIZE = 12
const TICK_HEIGHT = 14
const AXIS_PADDING_SHARE = 0.05
const POINT_RADIUS = 5
const NEWEST_RADIUS = 6.5
const LABEL_HEIGHT = 15
const LABEL_CHAR_WIDTH = 7.5
const NO_SCORE_WIDTH = NO_SCORE.length * LABEL_CHAR_WIDTH + 6
const NO_SCORE_TOP = 26
const GAP_LINE_TOP = 6
const GAP_DASH = [3, 4]

const colorsFor = (mode) => {
  const { palette } = getDesignTokens(mode === 'dark' ? 'dark' : 'light')
  return {
    line: palette.primary.main,
    text: palette.text.secondary,
    grid: palette.divider,
    surface: palette.background.paper,
  }
}

const labelBox = ({ value, element }, isNewest) => ({
  x: element.x,
  y: element.y - (isNewest ? NEWEST_RADIUS : POINT_RADIUS) - 4,
  width: String(value).length * LABEL_CHAR_WIDTH + 6,
})

const overlaps = (a, b) => Math.abs(a.x - b.x) < (a.width + b.width) / 2 && Math.abs(a.y - b.y) < LABEL_HEIGHT

const gapLabelBox = ({ chartArea }, x) => {
  const half = NO_SCORE_WIDTH / 2
  return {
    x: Math.min(Math.max(x, chartArea.left + half), chartArea.right - half),
    y: chartArea.top + NO_SCORE_TOP,
    width: NO_SCORE_WIDTH,
  }
}

const byNewest = (a, b) => b.position - a.position

const widestTick = (ctx, labels) => {
  ctx.save()
  ctx.font = `${TICK_FONT_SIZE}px ${ChartJS.defaults.font.family}`
  const widest = Math.max(...labels.map((label) => ctx.measureText(label).width))
  ctx.restore()
  return widest
}

const VALUE_LABELS = 'scoreValueLabels'

const valueLabels = {
  id: VALUE_LABELS,
  afterDatasetsDraw: (chart, args, { colors, gaps, positions }) => {
    const drawn = chart.data.datasets.flatMap((dataset, index) => {
      const meta = chart.getDatasetMeta(index)
      if (meta.hidden) return []
      return dataset.data.map(({ y }, position) => ({ value: y, position, element: meta.data[position] }))
    }).filter(({ value, element }) => value !== null && element && !element.skip)
    if (drawn.length === 0 && gaps.length === 0) return

    const { ctx, chartArea } = chart
    const newest = drawn.length > 0 ? Math.max(...drawn.map(({ position }) => position)) : null
    const scoreLabels = drawn.map((label) => ({
      text: String(label.value),
      position: label.position,
      bold: label.position === newest,
      box: labelBox(label, label.position === newest),
    }))
    const gapLabels = gaps.map((position) => {
      const x = chart.scales.x.getPixelForValue(positions[position])
      return { text: NO_SCORE, position, x, bold: false, box: gapLabelBox(chart, x) }
    })

    const placed = []
    const visible = new Set()
    ;[
      ...scoreLabels.filter(({ bold }) => bold),
      ...[...gapLabels].sort(byNewest),
      ...scoreLabels.filter(({ bold }) => !bold).sort(byNewest),
    ].forEach((label) => {
      if (placed.some((other) => overlaps(label.box, other))) return
      placed.push(label.box)
      visible.add(label)
    })

    ctx.save()
    ctx.lineWidth = 1.5
    ctx.strokeStyle = colors.text
    ctx.setLineDash(GAP_DASH)
    gapLabels.forEach(({ x }) => {
      ctx.beginPath()
      ctx.moveTo(x, chartArea.top + GAP_LINE_TOP)
      ctx.lineTo(x, chartArea.bottom)
      ctx.stroke()
    })
    ctx.restore()

    ctx.save()
    ctx.textAlign = 'center'
    ctx.textBaseline = 'bottom'
    ctx.lineJoin = 'round'
    ctx.lineWidth = 5
    ctx.strokeStyle = colors.surface
    ctx.fillStyle = colors.text
    ;[...scoreLabels, ...gapLabels].filter((label) => visible.has(label)).forEach(({ text, bold, box }) => {
      ctx.font = `${bold ? 700 : 500} 13px ${ChartJS.defaults.font.family}`
      ctx.strokeText(text, box.x, box.y)
      ctx.fillText(text, box.x, box.y)
    })
    ctx.restore()
  },
}

const PLUGINS = [valueLabels]

const ScoreChart = ({ points }) => {
  const { mode } = useThemeContext()
  const colors = colorsFor(mode)

  const multiYear = new Set(points.map((p) => p.day.slice(0, 4))).size > 1
  const newest = points.map(isScored).lastIndexOf(true)
  const segments = [...new Set(points.filter(isScored).map((p) => p.segment))]
  const gaps = points.flatMap((p, index) => (isScored(p) ? [] : [index]))
  const labels = points.map((p) => shortDay(p.day, multiYear))
  const positions = axisPositions(points.map((p) => p.day))
  const span = positions[positions.length - 1]
  const padding = Math.max(1, span * AXIS_PADDING_SHARE)
  const tightestGap = Math.min(...positions.slice(1).map((x, index) => x - positions[index]))

  const data = {
    datasets: segments.map((segment) => ({
      data: points.map((p, index) => ({
        x: positions[index],
        y: isScored(p) && p.segment === segment ? p.score : null,
      })),
      borderColor: colors.line,
      backgroundColor: colors.line,
      pointBackgroundColor: colors.line,
      pointBorderColor: colors.surface,
      pointBorderWidth: 2,
      pointRadius: points.map((p, index) => (index === newest ? NEWEST_RADIUS : POINT_RADIUS)),
      pointHoverRadius: points.map((p, index) => (index === newest ? NEWEST_RADIUS + 1 : POINT_RADIUS + 1)),
      borderWidth: 2.5,
      borderJoinStyle: 'round',
      borderCapStyle: 'round',
      tension: 0,
      spanGaps: false,
    })),
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    layout: { padding: { top: 22, right: 12, left: 4 } },
    plugins: {
      legend: { display: false },
      [VALUE_LABELS]: { colors, gaps, positions },
      tooltip: {
        callbacks: {
          title: (items) => formatDay(points[items[0]?.dataIndex]?.day),
          label: (context) => {
            const model = points[context.dataIndex]?.model
            return model ? `${context.parsed.y} (${model})` : `${context.parsed.y}`
          },
        },
      },
    },
    scales: {
      x: {
        type: 'linear',
        min: -padding,
        max: span + padding,
        afterBuildTicks: (scale) => {
          scale.ticks = positions.map((value) => ({ value }))
        },
        afterCalculateLabelRotation: (scale) => {
          const gap = (tightestGap / (span + 2 * padding)) * scale.width
          scale.labelRotation = Math.max(scale.labelRotation, labelRotation(gap, widestTick(scale.ctx, labels), TICK_HEIGHT))
        },
        ticks: {
          color: colors.text,
          autoSkip: false,
          minRotation: 0,
          maxRotation: 90,
          font: { size: TICK_FONT_SIZE },
          callback: (value, index) => labels[index],
        },
        grid: { display: false },
        border: { display: false },
      },
      y: {
        min: 300,
        max: 850,
        ticks: {
          stepSize: 100,
          color: colors.text,
          font: { size: 12 },
          callback: (value) => (value % 100 === 0 ? value : ''),
        },
        grid: { color: colors.grid },
        border: { display: false },
      },
    },
  }

  return (
    <div role="img" aria-label={chartLabel(points.length)} className="h-60 w-full min-w-0">
      <Line key={mode} data={data} options={options} plugins={PLUGINS} />
    </div>
  )
}

ScoreChart.propTypes = {
  points: PropTypes.array.isRequired,
}

export default ScoreChart
